import styled, { css } from 'styled-components';
import { getTheme, getThemeColor } from '../../styles/Theme';
import { TextBase } from '../../styles/FontStyles';
import { BREAKPOINT_LG, BREAKPOINT_MD, BREAKPOINT_SM } from '../../styles/General';
import { LinkHandler } from './Link';

interface PrimaryButtonProps {
  external?: boolean;
  href?: string;
  state?: {} & any;
  title?: string;
  target?: string;
  buttonInverted?: boolean;
  buttonShadow?: boolean;
}

export const PrimaryButton = styled.button.attrs((props: PrimaryButtonProps & JSX.IntrinsicElements['button']) => ({
  ...(props.href ? {} : { type: props.type || 'button' }),
  as: props.href ? LinkHandler : 'button',
  ...(props.href?.startsWith('/') ? { to: { pathname: props.href, state: props.state || {} } } : {}),
  ...(!props.href?.startsWith('/') ? { rel: 'noopener noreferrer nofollow' } : {}),
  ...(props.external ? { target: '_blank' } : {}),
  ...(props.disabled ? { tabIndex: -1 } : {}),
}))<PrimaryButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  min-height: 3.25rem;

  ${TextBase};
  font-size: 1rem;
  font-weight: 500;
  line-height: .9;
  letter-spacing: -.3px;
  text-transform: uppercase;
  text-decoration: none;

  color: ${({ buttonInverted = false }) => !buttonInverted ? getTheme(({ colors }) => colors.white) : getTheme(({ colors }) => colors.purple)};

  padding: 1rem;

  border: 1px solid ${getTheme(({ colors }) => colors.green)};
  border-radius: 50px;
  
  background-color: ${({ buttonInverted = false }) => !buttonInverted ? getTheme(({ colors }) => colors.purple) : getTheme(({ colors }) => colors.white)};
  box-shadow: -5px 5px 0 ${getTheme(({ colors }) => colors.green)};
  margin-bottom: 5px;
  margin-left: 5px;
  
  transition: color 150ms ease-out, background-color 150ms ease-out, border-color 150ms ease-out;
  cursor: pointer;

  ${({ buttonShadow = true }) => buttonShadow && css`
    filter: drop-shadow(0px 4px 4px ${getTheme(({ colors }) => colors.white)});
  `};

  ${BREAKPOINT_MD} {
    display: inline-flex;
    width: auto;
  }

  ${BREAKPOINT_LG} {
    font-size: 1.375rem;
    min-height: 3.5rem;
  }

  :hover {
    color: ${({ buttonInverted = false }) => !buttonInverted ? getTheme(({ colors }) => colors.green) : getTheme(({ colors }) => colors.pink)};
  }

  :active {
    ${({ buttonInverted = false }) => !buttonInverted ? (css`
      border-color: ${getTheme(({ colors }) => colors.white)};
      background-color: ${getTheme(({ colors }) => colors.white)};
      color: ${getTheme(({ colors }) => colors.purple)};
    `) : (css`
      border-color: ${getTheme(({ colors }) => colors.purple)};
      background-color: ${getTheme(({ colors }) => colors.purple)};
      color: ${getTheme(({ colors }) => colors.white)};
    `)}
  }

  :disabled {
    background-color: rgba(30, 30, 30, .7);
    border-color: rgba(30, 30, 30, .7);
    box-shadow: -5px 5px 0 rgba(30, 30, 30, .3);
    color: rgba(230, 230, 230, .3);
    pointer-events: none;
    user-select: none;
  }
`;

export const SecondaryButton = styled(PrimaryButton).attrs((props: PrimaryButtonProps) => ({ buttonInverted: props.buttonInverted !== undefined ? props.buttonInverted : true }))``;

export const BoxedButton = styled.button.attrs(({ type }: any) => ({ type: type || 'button' }))<{ type?: 'button' | 'submit' }>`
  display: flex;
  align-items: center;
  justify-content: center;  
  
  height: 3.125rem;
  padding: 0 1rem;
  
  font-size: 1rem;
  line-height: 1;
  font-weight: bold;
  
  color: ${getTheme(({ colors }) => colors.purple)};
  background: none;
  
  border: 1px solid ${getTheme(({ colors }) => colors.green)};
  border-top: none;
  
  &:hover:not(:disabled) {
    cursor: pointer;
    background: ${getTheme(({ colors }) => colors.purple)};
    color: ${getTheme(({ colors }) => colors.green)};
    transition: background-color .15s ease-out, color .15s ease-out;
  }

  &:disabled {
    background-color: ${getThemeColor('gray')};
    color: ${getThemeColor('darkGray')};
  }
  
  ${BREAKPOINT_SM} {
    border: 1px solid ${getTheme(({ colors }) => colors.green)};
    border-left: none;
  }
`;